import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Encapsulates all data fetching and mutation for the AssetDetail page.
 * Returns asset data, indicators, alerts, prediction, and action handlers.
 */
export function useAssetDetail(symbol) {
  const { isAuthenticated, hasSubscription, predictionCredits, authHeaders } = useAuth();

  const [assetData, setAssetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prediction, setPrediction] = useState(null);
  const [predictionLoading, setPredictionLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [chartDays, setChartDays] = useState(90);
  const [ohlcData, setOhlcData] = useState(null);
  const [indicatorData, setIndicatorData] = useState(null);

  const fetchAssetDetails = useCallback(async (days = 30) => {
    try {
      const response = await axios.get(`${API}/market/${symbol}?days=${days}`);
      setAssetData(response.data);
      setLoading(false);
      return response.data;
    } catch (error) {
      console.error("Error fetching asset details:", error);
      toast.error("Failed to fetch asset details");
      setLoading(false);
      return null;
    }
  }, [symbol]);

  const fetchAlerts = useCallback(async () => {
    try {
      const headers = authHeaders();
      if (!headers.Authorization) { setAlerts([]); return; }
      const response = await axios.get(`${API}/alerts?active_only=true`, { headers });
      const symbolAlerts = response.data.alerts.filter(a => a.symbol === symbol);
      setAlerts(symbolAlerts);
    } catch (error) {
      console.error("Error fetching alerts:", error);
    }
  }, [symbol, authHeaders]);

  const fetchIndicators = useCallback(async (days = 90, customParams = {}) => {
    try {
      const queryParams = new URLSearchParams({
        days: days.toString(),
        ...Object.fromEntries(Object.entries(customParams).map(([k, v]) => [k, String(v)])),
      });
      const response = await axios.get(`${API}/indicators/${symbol}?${queryParams}`);
      setOhlcData(response.data.ohlc);
      setIndicatorData(response.data.indicators);
    } catch (error) {
      console.error("Error fetching indicators:", error);
    }
  }, [symbol]);

  useEffect(() => {
    fetchAssetDetails(chartDays);
    fetchAlerts();
    fetchIndicators(chartDays);
  }, [symbol, chartDays, fetchAssetDetails, fetchAlerts, fetchIndicators]);

  const generatePrediction = useCallback(async (timeframe, onAuthRequired, onPaymentRequired) => {
    if (!isAuthenticated) { onAuthRequired(); return; }
    if (!hasSubscription && predictionCredits <= 0) { onPaymentRequired(); return; }
    if (!assetData) return;

    setPredictionLoading(true);
    try {
      const response = await axios.post(
        `${API}/predictions/generate`,
        { symbol, timeframe, asset_type: assetData.asset.type },
        { headers: authHeaders() }
      );
      setPrediction(response.data);
      toast.success("AI prediction generated!");
    } catch (error) {
      console.error("Error generating prediction:", error);
      if (error.response?.status === 402) {
        toast.error("Insufficient credits");
        onPaymentRequired();
      } else if (error.response?.status === 401) {
        toast.error("Please sign in to continue");
        onAuthRequired();
      } else {
        toast.error("Failed to generate prediction");
      }
    } finally {
      setPredictionLoading(false);
    }
  }, [symbol, assetData, isAuthenticated, hasSubscription, predictionCredits, authHeaders]);

  const addToPortfolio = useCallback(async (quantity, buyPrice) => {
    if (!quantity || !buyPrice) {
      toast.error("Please fill in all fields");
      return false;
    }
    try {
      await axios.post(`${API}/portfolio/add`, {
        symbol: assetData.asset.symbol,
        name: assetData.asset.name,
        quantity: parseFloat(quantity),
        buy_price: parseFloat(buyPrice),
        asset_type: assetData.asset.type,
      }, { headers: authHeaders() });
      toast.success("Added to portfolio!");
      return true;
    } catch (error) {
      console.error("Error adding to portfolio:", error);
      if (error.response?.status === 401) toast.error("Please sign in to add to portfolio");
      else toast.error("Failed to add to portfolio");
      return false;
    }
  }, [assetData, authHeaders]);

  const createAlert = useCallback(async (condition, targetPrice, repeat = false) => {
    if (!targetPrice) {
      toast.error("Please enter target price");
      return false;
    }
    try {
      await axios.post(`${API}/alerts/create`, {
        symbol,
        condition,
        target_price: parseFloat(targetPrice),
        repeat,
      }, { headers: authHeaders() });
      toast.success("Price alert created!");
      fetchAlerts();
      return true;
    } catch (error) {
      console.error("Error creating alert:", error);
      if (error.response?.status === 401) toast.error("Please sign in to create alerts");
      else toast.error("Failed to create alert");
      return false;
    }
  }, [symbol, fetchAlerts, authHeaders]);

  const deleteAlert = useCallback(async (alertId) => {
    try {
      await axios.delete(`${API}/alerts/${alertId}`, { headers: authHeaders() });
      toast.success("Alert deleted");
      fetchAlerts();
    } catch (error) {
      console.error("Error deleting alert:", error);
      toast.error("Failed to delete alert");
    }
  }, [fetchAlerts, authHeaders]);

  return {
    assetData,
    loading,
    prediction,
    predictionLoading,
    alerts,
    chartDays,
    setChartDays,
    ohlcData,
    indicatorData,
    fetchIndicators,
    generatePrediction,
    addToPortfolio,
    createAlert,
    deleteAlert,
  };
}
